import { useMutation, useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useState } from 'react';
import { BackendClient } from '~/backend/backend-client';
import { ContouringClient } from '~/backend/contouring-client';
import { DoseClient } from '~/backend/dose-client';
import { PollData, ResultID } from '~/backend/types';
import { handleError } from '~/utils/errors';

export type ClientParam = ContouringClient | DoseClient;

/**
 * A React hook that implements polling logic to repeatedly query a backend service for results.
 * This hook is used to continuously check the status of a process and retrieve the result once available.
 *
 * @param {BackendClient} client - The client used to perform the polling operation against the backend service.
 * @param {Object} callbacks - Callbacks object for the hook.
 * @param {Function} callbacks.onResult - A callback function that gets executed when the polling operation
 * retrieves a result with a status indicating completion.
 *
 * @returns {Object} An object containing the polling query state and control functions.
 * - @returns {Function} startPolling: A function to start the polling process at a specified interval in milliseconds.
 * - @returns {Function} stopPolling: A function to stop the polling process.
 * - @returns {UseQueryResult<void, Error>} methods from `useQuery` providing access to the query's
 *
 */
export const usePollingQuery = (
    client: BackendClient,
    { onResult }: { onResult: (result: PollData) => void }
) => {
    const [pollInterval, setPollInterval] = useState<number>();

    const startPolling = useCallback((interval: number) => {
        setPollInterval(interval);
    }, []);

    const stopPolling = useCallback(() => {
        setPollInterval(undefined);
    }, []);
    const query = useQuery({
        queryKey: ['pollingData'],
        queryFn: async () => {
            try {
                const result = await client.poll();
                if (result.status !== 'HAS_RESULT') {
                    return null;
                } else {
                    stopPolling();
                    onResult(result);
                }
                return result;
            } catch (error) {
                handleError(error, 'Error when polling the data.');
                return null;
            }
        },
        retry: true,
        retryDelay: 5 * 1000,
        refetchInterval: pollInterval,
        enabled: !!pollInterval,
    });

    return { ...query, startPolling, stopPolling };
};

/**
 * A React hook used for processing the results after data has been retrieved.
 * This hook handles the download and aggregation of files based on result IDs, and also processes
 * any errors identified during the download or result retrieval process.
 *
 * @param {BackendClient} params.client - The client used to perform network requests, including file downloads
 * and error retrievals associated with the processing results.
 * @param {Object} callbacks - callback object for the hook.
 * @param {Function} callbacks.onSuccess - A callback function that is executed when all files have been successfully processed.
 * @param {Function} callbacks.onError - A callback function that is executed when errors occur during processing.
 *
 * @returns {Function} handleProcessedResults - A function that processes the results from a poll.
 * It takes `PollData` as a parameter and performs the following actions:
 * - Downloads files associated with the result IDs present in the polled data.
 * - Gathers and logs errors from both the download process and the result data itself.
 * - Calls the `onSuccess` callback with an array of `File` objects once all files have been processed.
 * - Calls the `onError` callback with an array of error messages if any errors occurred.
 *
 */
export function useProcessedResults(
    client: BackendClient,
    {
        onSuccess,
        onError,
    }: {
        onSuccess(files: File[]): void;
        onError(errors: string[]): void;
    }
) {
    const { mutateAsync: download } = useResultDownload(client);

    const processResultIDs = async (resultIDs: ResultID[]): Promise<File[]> => {
        const files: File[] = [];

        for (const item of resultIDs) {
            const resultFiles = await downloadResultFiles(item);
            files.push(...resultFiles);
            client.deleteResult(item.id);
        }

        return files;
    };

    const downloadResultFiles = async (item: ResultID): Promise<File[]> => {
        try {
            const results = await Promise.all(
                item.file_ids.map(async (path) =>
                    download({ resultID: item.id, path })
                )
            );
            return results.filter((file): file is File => file !== undefined);
        } catch (error) {
            handleError(
                error,
                `Failed to download files for result ID ${item.id}`
            );
            return [];
        }
    };

    const processErrorIDs = async (errorIDs: ResultID[]): Promise<string[]> => {
        const errors: string[] = [];
        for (const item of errorIDs) {
            try {
                const error = await client.getErrors(item.id);
                if (error) {
                    errors.push(error);
                }
            } catch (error) {
                handleError(
                    error,
                    `Failed to process error with ID ${item.id}`
                );
            }
        }

        return errors;
    };

    const handleProcessedResults = async (data: PollData) => {
        if (!data.result) {
            enqueueSnackbar(`No result data available`, {
                variant: 'error',
            });
            onError(['No result data available']);
            return;
        }

        const files = await processResultIDs(data.result.result_ids_v2);
        const errors = await processErrorIDs(data.result.error_ids_v2);
        if (errors.length > 0) {
            for (const errorMsg of errors) {
                enqueueSnackbar(`Error processing file: ${errorMsg}`, {
                    variant: 'error',
                });
            }
            onError(errors);
        } else {
            onSuccess(files);
        }
    };

    return handleProcessedResults;
}

/**
 * A React hook that provides functionality to download a file based on the given result ID and file path.
 * It handles the download operation with automatic retries upon failure.
 *
 * @param {BackendClient} client - The client used to interact with the backend services for downloading the file.
 *
 * @returns A mutation result object
 */
export const useResultDownload = (client: BackendClient) => {
    return useMutation({
        retry: 5,
        retryDelay: 1000,
        mutationFn: async (req: { resultID: string; path: string }) => {
            const blob = await client.downloadFile(req.resultID, req.path);
            if (blob) {
                const file = new File([blob], req.path);
                return file;
            } else {
                throw new Error('No blob downloaded!');
            }
        },
    });
};
